import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import FCLogo from "../../Assets/Images/fucascore_png.png";

const FSAdminAddTeams = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state (kreiranje / uređivanje)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [teamLogo, setTeamLogo] = useState(null); // može biti File ili string (URL postojećeg loga)
  const [isUploading, setIsUploading] = useState(false);

  // State za odabir postojećeg loga iz baze
  const [isLogoPickerOpen, setIsLogoPickerOpen] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/admin-login");
        return;
      }
      const { data: isAdmin } = await supabase
        .from("admin_users")
        .select("id")
        .eq("id", user.id)
        .single();

      if (!isAdmin) {
        navigate("/");
      } else {
        setLoading(false);
        fetchGlobalTeams();
      }
    };
    checkAccess();
  }, [navigate]);

  const fetchGlobalTeams = async () => {
    const { data, error } = await supabase
      .from("admin_all_teams")
      .select("*")
      .order("name", { ascending: true });

    if (!error && data) {
      setTeams(data);
    }
  };

  const compressImage = (file, maxWidth = 300, maxHeight = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], `${Date.now()}.webp`, {
                  type: "image/webp",
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Kompresija nije uspjela."));
              }
            },
            "image/webp",
            0.8,
          );
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const openCreateModal = () => {
    setEditingTeamId(null);
    setTeamName("");
    setTeamLogo(null);
    setIsModalOpen(true);
  };

  const openEditModal = (team) => {
    setEditingTeamId(team.id);
    setTeamName(team.name);
    setTeamLogo(team.logo_url); // Postavi postojeći URL ako ga ima
    setIsModalOpen(true);
  };

  const handleSaveTeam = async () => {
    if (!teamName.trim()) return alert("Unesite naziv ekipe!");

    setIsUploading(true);
    try {
      let finalLogoUrl = typeof teamLogo === "string" ? teamLogo : null;

      // Ako je odabran novi fajl sa uređaja, komprimiraj ga i uplodaj
      if (teamLogo && typeof teamLogo !== "string") {
        const compressedFile = await compressImage(teamLogo);
        const fileName = `${Math.random()}-${Date.now()}.webp`;
        const filePath = `global-team-logos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("logos")
          .upload(filePath, compressedFile);

        if (uploadError) throw uploadError;

        const { data: publicData } = supabase.storage
          .from("logos")
          .getPublicUrl(filePath);

        finalLogoUrl = publicData.publicUrl;
      }

      if (editingTeamId) {
        // Ažuriranje postojećeg tima
        const { error } = await supabase
          .from("admin_all_teams")
          .update({
            name: teamName.trim(),
            logo_url: finalLogoUrl,
          })
          .eq("id", editingTeamId);

        if (error) throw error;
      } else {
        // Kreiranje novog tima
        const { error } = await supabase.from("admin_all_teams").insert([
          {
            name: teamName.trim(),
            logo_url: finalLogoUrl,
          },
        ]);

        if (error) throw error;
      }

      setTeamName("");
      setTeamLogo(null);
      setEditingTeamId(null);
      setIsModalOpen(false);
      fetchGlobalTeams();
    } catch (err) {
      alert("Greška kod spremanja: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const filteredTeams = teams.filter((t) =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading)
    return (
      <div
        className="fat-global-wrapper"
        style={{ color: "#fff", padding: "20px" }}
      >
        Učitavanje...
      </div>
    );

  return (
    <div className="fat-global-wrapper">
      {/* Navigacija */}
      <nav className="fat-navbar">
        <a href="/fucascore-admin">
          <img src={FCLogo} alt="FucaScore logo" className="fat-logo" />
        </a>
        <div className="fat-nav-title">FucaScore Teo</div>
        <button
          className="fat-logout-btn"
          onClick={async () => {
            await supabase.auth.signOut();
            navigate("/admin-login");
          }}
        >
          Odjava
        </button>
      </nav>

      {/* Zaglavlje i gumb nazad */}
      <div className="fat-header-row">
        <button
          className="fat-back-btn"
          onClick={() => navigate("/fucascore-admin")}
        >
          <i className="fi fi-rs-angle-left"></i> Nazad
        </button>
      </div>

      {/* Sekcija za dodavanje */}
      <div className="fat-add-section">
        <h2>Globalne Ekipe (Baza)</h2>
        <div className="fat-add-box" onClick={openCreateModal}>
          <div className="fat-circle-plus">+</div>
          <span>Dodaj novu ekipu</span>
        </div>
      </div>

      {/* Tražilica i lista */}
      <div className="fat-teams-container">
        <div className="fat-search-box-wrapper">
          <i className="fi fi-rs-search fat-search-icon"></i>
          <input
            type="text"
            className="fat-search-input"
            placeholder="Pretraži globalne ekipe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="fat-clear-search-btn"
              onClick={() => setSearchTerm("")}
              title="Obriši pretragu"
            >
              <i className="fi fi-rs-cross"></i>
            </button>
          )}
        </div>

        {filteredTeams.map((team) => (
          <div key={team.id} className="fat-team-card">
            <div className="fat-card-info">
              <img
                src={team.logo_url || FCLogo}
                alt={team.name}
                className="fat-team-logo"
              />
              <h3>{team.name}</h3>
            </div>
            <button
              className="fat-edit-icon-btn"
              onClick={() => openEditModal(team)}
              title="Uredi tim"
            >
              <i className="fi fi-rs-pencil"></i>
            </button>
          </div>
        ))}
      </div>

      {/* Modal za dodavanje / uređivanje */}
      {isModalOpen && (
        <div
          className="fat-modal-overlay"
          onClick={() => setIsModalOpen(false)}
        >
          <div className="fat-modal-box" onClick={(e) => e.stopPropagation()}>
            <h2>{editingTeamId ? "Uredi ekipu" : "Nova Globalna Ekipa"}</h2>
            <div className="fat-modal-stack">
              <div className="fat-form-group">
                <label>Naziv Ekipe</label>
                <input
                  type="text"
                  placeholder="npr. AGRAD PROJEKT"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />
              </div>

              <div className="fat-form-group">
                <label>Grb / Logo</label>

                {/* Opcije za dodavanje loga: Datoteka ili Odabir postojećeg */}
                <div className="fat-logo-options-row">
                  <label
                    htmlFor="fat-logo-upload"
                    className="fat-custom-file-btn"
                  >
                    <i className="fi fi-rs-plus-small"></i> Učitaj sa uređaja
                  </label>
                  <input
                    id="fat-logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setTeamLogo(e.target.files[0])}
                    style={{ display: "none" }}
                  />

                  <button
                    type="button"
                    className="fat-pick-existing-btn"
                    onClick={() => setIsLogoPickerOpen(true)}
                  >
                    <i className="fi fi-rs-apps"></i> Odaberi postojeći logo
                  </button>
                </div>

                {/* Pregled odabranog loga */}
                {teamLogo && (
                  <div className="fat-logo-preview-box">
                    <img
                      src={
                        typeof teamLogo === "string"
                          ? teamLogo
                          : URL.createObjectURL(teamLogo)
                      }
                      alt="Preview"
                    />
                    <span>
                      {typeof teamLogo === "string"
                        ? "Odabran postojeći logo"
                        : "Nova slika odabrana (bit će komprimirana)"}
                    </span>
                    <button
                      type="button"
                      className="fat-remove-preview"
                      onClick={() => setTeamLogo(null)}
                    >
                      <i className="fi fi-rs-cross"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="fat-modal-actions">
                <button
                  className="fat-btn-save"
                  onClick={handleSaveTeam}
                  disabled={isUploading}
                >
                  {isUploading ? "Spremanje..." : "Spremi u bazu"}
                </button>
                <button
                  className="fat-btn-cancel"
                  onClick={() => setIsModalOpen(false)}
                >
                  Odustani
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Podmodal za biranje postojećeg loga iz baze */}
      {isLogoPickerOpen && (
        <div
          className="fat-modal-overlay"
          onClick={() => setIsLogoPickerOpen(false)}
        >
          <div
            className="fat-modal-box fat-picker-box"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Odaberi logo iz baze</h2>
            <p className="fat-picker-subtitle">
              Klikni na logo ekipe čiju sliku želiš iskoristiti:
            </p>

            <div className="fat-picker-grid">
              {teams
                .filter((t) => t.logo_url)
                .map((t) => (
                  <div
                    key={t.id}
                    className="fat-picker-item"
                    onClick={() => {
                      setTeamLogo(t.logo_url);
                      setIsLogoPickerOpen(false);
                    }}
                  >
                    <img src={t.logo_url} alt={t.name} />
                    <span>{t.name}</span>
                  </div>
                ))}
            </div>

            <button
              className="fat-btn-cancel"
              style={{ width: "100%", marginTop: "15px" }}
              onClick={() => setIsLogoPickerOpen(false)}
            >
              Zatvori
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FSAdminAddTeams;
